import { Button, Group, type MantineColor, type MantineSpacing } from '@mantine/core'
import type { ReactNode } from 'react'

import { useHideFab } from './Fab'

/**
 * The row that ends a form or dialog: Cancel (subtle) then the main action on the right, with an optional [start]
 * slot on the left (Delete, Duplicate, "Copy to custom food"). Without [onSubmit] the main button submits the form
 * it sits in (or [form]); [submitLabel] null leaves only Cancel, as a dialog that just closes. [sticky] pins it to the
 * bottom of a long editor page — its own bottom action row, so it hides the shared FAB rather than sit under or
 * behind it (owner device test, 24 Oct 2026: the "+" must never cover anything). A no-op outside `Layout.tsx`'s
 * shell (guided setup, a modal/sheet already above the FAB) since `useHideFab` only does something inside it.
 */
export function FormActions(props: {
  submitLabel?: ReactNode | null
  onSubmit?: () => void
  form?: string
  loading?: boolean
  disabled?: boolean
  onCancel?: (() => void) | undefined
  cancelLabel?: ReactNode
  start?: ReactNode
  /** Other actions beside the main one, on the right ("Use the default"). */
  extra?: ReactNode
  sticky?: boolean
  mt?: MantineSpacing
  /** A confirmation's main action can take a colour (red for a delete) and the dialog's first focus. */
  submitColor?: MantineColor
  submitAutoFocus?: boolean
}) {
  useHideFab(props.sticky === true)
  const submit =
    props.submitLabel === null ? null : (
      <Button
        type={props.onSubmit ? 'button' : 'submit'}
        form={props.form}
        onClick={props.onSubmit}
        loading={props.loading}
        disabled={props.disabled}
        color={props.submitColor}
        data-autofocus={props.submitAutoFocus || undefined}
      >
        {props.submitLabel ?? 'Save'}
      </Button>
    )
  const end = (
    <Group gap="xs" wrap="wrap" justify="flex-end">
      {props.extra}
      {props.onCancel && (
        <Button variant="subtle" onClick={props.onCancel}>
          {props.cancelLabel ?? 'Cancel'}
        </Button>
      )}
      {submit}
    </Group>
  )
  return (
    <Group justify={props.start ? 'space-between' : 'flex-end'} wrap="wrap" gap="xs" mt={props.mt} className={props.sticky ? 'sticky-footer' : undefined}>
      {props.start && (
        <Group gap="xs" wrap="wrap">
          {props.start}
        </Group>
      )}
      {end}
    </Group>
  )
}
